import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { isType } from '@sorare/core/src/lib/gql';
import { MonetaryAmountFiatCurrency } from '@sorare/core/src/lib/monetaryAmount';

import { CardLayout } from '../../CardLayout';
import { StreakProgressAnimated } from './StreakProgressAnimated';
import { StreakRewardAnimated } from './StreakRewardAnimated';
import type { StreakCard_thresholdsStreakTask } from './__generated__/index.graphql';

const LeagueHeader = styled.img`
  width: 100%;
  max-height: calc(5 * var(--unit));
`;
const Wrapper = styled.div`
  width: 100%;
  padding: var(--triple-unit);
`;

type Props = {
  completedStreakTask: StreakCard_thresholdsStreakTask;
  focused?: boolean;
  delay?: number;
};
export const StreakCard = ({
  completedStreakTask,
  focused,
  delay = 0,
}: Props) => {
  const { completedBy, currentThreshold } = completedStreakTask;

  if (!completedBy) {
    return null;
  }

  const { managerTeam, so5Leaderboard } = completedBy;
  const { so5League } = so5Leaderboard || {};
  const reward = currentThreshold.rewardConfigs.find(
    rewardConfig => 'amount' in rewardConfig
  );

  return (
    <CardLayout
      so5Lineup={completedBy}
      bgRgbColor="255, 78, 34"
      borderColor="linear-gradient(323deg, #FEF314 -3.1%, #FF4E22 52.7%)"
      delay={delay}
      topLeftIcon={null}
      icon={
        isType(reward, 'MonetaryRewardConfig') && (
          <StreakRewardAnimated
            delay={delay + 300}
            score={currentThreshold.score}
            monetaryReward={
              reward.amount[
                reward.amount.referenceCurrency.toLowerCase() as MonetaryAmountFiatCurrency
              ]
            }
          />
        )
      }
      title={managerTeam?.name}
      footer={<LeagueHeader src={so5League?.headerUrl} alt="" />}
      withFire
    >
      <Wrapper>
        <StreakProgressAnimated
          completedStreakTask={completedStreakTask}
          animate={focused}
          delay={delay + 1000}
        />
      </Wrapper>
    </CardLayout>
  );
};

StreakCard.fragments = {
  thresholdsStreakTask: gql`
    fragment StreakCard_thresholdsStreakTask on ThresholdsStreakTask {
      id
      currentThreshold {
        score
        rewardConfigs {
          id
          ... on MonetaryRewardConfig {
            id
            amount {
              eur
              usd
              gbp
              referenceCurrency
            }
          }
        }
      }
      completedBy {
        id
        managerTeam {
          id
          name
        }
        so5Leaderboard {
          slug
          seasonality
          seasonalityName
          so5League {
            slug
            headerUrl
          }
        }
        ...CardLayout_so5lineup
      }
      ...StreakProgressAnimated_thresholdsStreakTask
    }
    ${CardLayout.fragments.so5Lineup}
    ${StreakProgressAnimated.fragments.thresholdsStreakTask}
  ` as TypedDocumentNode<StreakCard_thresholdsStreakTask>,
};
